"use client";

/**
 * The composer's right-hand controls: the active-feature chips, the model
 * selector, the voice microphone, the speech-recognition error and the send /
 * stop button.
 *
 * ⚠️ The send and stop buttons are painted by two mechanisms: the rule in
 * `heroui-v2-compat.css` matched on their `data-id`, and `useSendButtonStyle`.
 * Keep both `data-id` values exactly as they are.
 */

import type { TFunction } from "i18next";

import { Button, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useSendButtonStyle } from "@/hooks/useSendButtonStyle";

import { ChatModelSelector } from "./ChatModelSelector";

// ── Active-feature chips ───────────────────────────────────────────────

interface WebSearchChipProps {
  onWebSearchToggle: (val: boolean) => void;
}

const WebSearchChip = ({ onWebSearchToggle }: WebSearchChipProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          isIconOnly
          aria-label={t("chatInput.chips.webSearchTooltip")}
          className="w-6 h-6 min-w-6 rounded-full border bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-500/15 dark:text-green-300 dark:border-green-400/40 dark:hover:bg-green-500/25"
          data-id="web-search-chip"
          size="sm"
          variant="ghost"
          onPress={() => onWebSearchToggle(false)}
        >
          <Icon icon="solar:global-linear" width={12} />
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow className="max-w-70" placement="top">
        {t("chatInput.chips.webSearchTooltip")}
      </TooltipContent>
    </Tooltip>
  );
};

interface KnowledgeHubChipProps {
  onKnowledgeHubToggle: () => void;
}

const KnowledgeHubChip = ({ onKnowledgeHubToggle }: KnowledgeHubChipProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          isIconOnly
          aria-label={t("chatInput.chips.knowledgeHubTooltip")}
          className="w-6 h-6 min-w-6 rounded-full border bg-primary-50 text-primary-600 border-primary-200 hover:bg-primary-100 dark:bg-primary-500/15 dark:text-primary-300 dark:border-primary-500/40 dark:hover:bg-primary-500/25"
          data-id="knowledge-hub-chip"
          size="sm"
          variant="ghost"
          onPress={onKnowledgeHubToggle}
        >
          <Icon icon="solar:database-linear" width={12} />
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow className="max-w-70" placement="top">
        {t("chatInput.chips.knowledgeHubTooltip")}
      </TooltipContent>
    </Tooltip>
  );
};

interface ActiveFeatureChipsProps {
  /** The "Show active feature indicators" preference. */
  show: boolean;
  isSearchEnabled: boolean;
  isKnowledgeHubEnabled: boolean;
  onWebSearchToggle: (val: boolean) => void;
  onKnowledgeHubToggle: () => void;
}

/**
 * Active-feature indicators — icon-only, shown when the "Show active feature
 * indicators" setting is on.
 */
export const ActiveFeatureChips = ({
  show,
  isSearchEnabled,
  isKnowledgeHubEnabled,
  onWebSearchToggle,
  onKnowledgeHubToggle,
}: ActiveFeatureChipsProps) => {
  if (!(show && (isSearchEnabled || isKnowledgeHubEnabled))) return null;

  return (
    <div className="flex items-center gap-1" data-id="active-features-chips">
      {isSearchEnabled && <WebSearchChip onWebSearchToggle={onWebSearchToggle} />}

      {isKnowledgeHubEnabled && <KnowledgeHubChip onKnowledgeHubToggle={onKnowledgeHubToggle} />}
    </div>
  );
};

// ── Model selector ─────────────────────────────────────────────────────

interface ModelSelectorSlotProps {
  visible: boolean;
}

/** Model selector — visible by default (modelSelector pinned true), hideable via Settings. */
export const ModelSelectorSlot = ({ visible }: ModelSelectorSlotProps) =>
  visible ? <ChatModelSelector /> : null;

// ── Voice ──────────────────────────────────────────────────────────────

/** The voice button's label, which its tooltip repeats. */
const voiceLabel = (t: TFunction, isPromptLocked: boolean, listening: boolean): string =>
  isPromptLocked
    ? t("Voice input disabled for this workflow")
    : listening
      ? t("Stop voice recording")
      : t("Start voice recording");

interface VoiceButtonProps {
  showVoice: boolean;
  browserSupportsSpeechRecognition: boolean;
  isPromptLocked: boolean;
  listening: boolean;
  onVoicePress: () => void;
}

/** Voice recording. */
export const VoiceButton = ({
  showVoice,
  browserSupportsSpeechRecognition,
  isPromptLocked,
  listening,
  onVoicePress,
}: VoiceButtonProps) => {
  const { t } = useTranslation();

  if (!(showVoice && browserSupportsSpeechRecognition)) return null;

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          isIconOnly
          aria-label={voiceLabel(t, isPromptLocked, listening)}
          className="rounded-full border border-default-300 bg-default-100 hover:bg-default-200 disabled:bg-default-200 disabled:text-default-400 disabled:cursor-not-allowed"
          data-id="voice-button"
          isDisabled={isPromptLocked}
          size="sm"
          variant="ghost"
          onPress={onVoicePress}
        >
          <Icon
            className={isPromptLocked ? "text-default-400" : "text-default-500"}
            icon={listening ? "solar:stop-circle-linear" : "solar:microphone-linear"}
            width={18}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>{voiceLabel(t, isPromptLocked, listening)}</TooltipContent>
    </Tooltip>
  );
};

interface SpeechErrorProps {
  error: string | null;
}

/** Speech error. */
export const SpeechError = ({ error }: SpeechErrorProps) =>
  error ? (
    <div className="text-danger text-sm" role="alert">
      {error}
    </div>
  ) : null;

// ── Send / stop ────────────────────────────────────────────────────────

/**
 * What changes between the stop button (while streaming) and the send button.
 * Both `data-id` values are matched by the brand rule in `heroui-v2-compat.css`.
 */
const STOP_BUTTON = {
  dataId: "stop-streaming-button",
  type: "button",
  icon: "solar:stop-circle-linear",
} as const;

const SEND_BUTTON = {
  dataId: "send-message-button",
  type: "submit",
  icon: "solar:arrow-up-linear",
} as const;

/** The send / stop button's label, which its tooltip repeats. */
const sendLabel = (t: TFunction, streaming: boolean): string =>
  streaming ? t("Stop streaming") : t("Send message");

/** The send / stop icon's colour: danger while streaming, muted while empty. */
const sendIconClassName = (streaming: boolean, prompt: string) =>
  cn(
    "[&>path]:stroke-[2px]",
    streaming
      ? "text-danger-foreground"
      : !prompt
        ? "text-default-600"
        : // ready: inherit the button's contrast-safe foreground
          undefined,
  );

interface SendStopButtonProps {
  prompt: string;
  streaming: boolean;
  isUploading: boolean;
  isUploadingFiles: boolean;
  onStop: () => void;
}

/** Send / Stop button. */
export const SendStopButton = ({
  prompt,
  streaming,
  isUploading,
  isUploadingFiles,
  onStop,
}: SendStopButtonProps) => {
  const { t } = useTranslation();

  /**
   * Brand-coloured inline style for the send button when a prompt is ready.
   *
   * The argument must stay the exact negation of the button's own `isDisabled`
   * below. Passing only `prompt` painted a button that was disabled because a
   * file was still uploading, which is the one state the hook documents itself
   * as staying out of.
   */
  const sendButtonStyle = useSendButtonStyle(
    !streaming && Boolean(prompt) && !isUploading && !isUploadingFiles,
  );
  const mode = streaming ? STOP_BUTTON : SEND_BUTTON;
  const waitingForUploads = !streaming && (isUploading || isUploadingFiles);

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          isIconOnly
          aria-label={sendLabel(t, streaming)}
          className="rounded-full"
          data-id={mode.dataId}
          isDisabled={streaming ? false : !prompt || isUploading || isUploadingFiles}
          size="sm"
          style={sendButtonStyle}
          type={mode.type}
          variant="primary"
          onPress={streaming ? onStop : undefined}
        >
          <Icon className={sendIconClassName(streaming, prompt)} icon={mode.icon} width={20} />
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>
        {waitingForUploads ? t("chatInput.sendWaitingForUploads") : sendLabel(t, streaming)}
      </TooltipContent>
    </Tooltip>
  );
};
